import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Envoyer l'email via notre fonction edge
      const emailResponse = await fetch('https://dcneykqhasdgepewqshp.supabase.co/functions/v1/send-email-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'contact',
          formData: formData
        })
      });

      if (emailResponse.ok) {
        // Envoyer aussi au formulaire Readdy
        const formDataToSend = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          formDataToSend.append(key, value);
        });

        await fetch('https://readdy.ai/api/form/d4515u1iniomskj1e820', {
          method: 'POST',
          body: formDataToSend
        });

        setSubmitStatus('Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: ''
        });
      } else {
        setSubmitStatus('Une erreur est survenue lors de l\'envoi de l\'email. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitStatus('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('');

    if (!newsletterEmail) {
      setNewsletterStatus('Veuillez saisir votre adresse email.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d4515u1iniomskj1e82g', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setNewsletterStatus('Inscription réussie ! Merci de vous être abonné à notre newsletter.');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      setNewsletterStatus('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 to-red-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Contactez-nous
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vous avez un projet d'événement ? Une question sur nos prestations ? 
              Notre équipe est là pour vous accompagner dans la réalisation de vos ambitions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Formulaire de contact
              </h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Votre nom et prénom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div>
                  <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="evenement-culturel">Événement culturel</option>
                    <option value="evenement-institutionnel">Événement institutionnel</option>
                    <option value="experience-immersive">Expérience immersive</option>
                    <option value="pavillon-afrique">Pavillon Afrique</option>
                    <option value="festival-afroverso">Festival Afroverso</option>
                    <option value="partenariat">Partenariat</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                    placeholder="Décrivez votre projet ou votre demande..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 caractères
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </button>

                {submitStatus && (
                  <div className={`p-4 rounded-lg text-sm ${
                    submitStatus.includes('succès') 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {submitStatus}
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Coordonnées */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Nos coordonnées
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-orange-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                      <p className="text-gray-600">
                        Île de Nantes<br />
                        44000 Nantes, France
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-orange-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                      <p className="text-gray-600">+33 6 58 86 79 19</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-orange-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <p className="text-gray-600">contact@ideevents.fr</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-time-line text-orange-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Horaires</h3>
                      <p className="text-gray-600">
                        Lundi - Vendredi : 9h00 - 18h00<br />
                        Samedi : 10h00 - 16h00
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Suivez-nous
                </h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="https://www.instagram.com/ide_events?utm_source=qr&igsh=ZXhud2dta2l3Yjlo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-instagram-line text-pink-600 text-2xl"></i>
                    </div>
                    <span className="font-medium text-gray-900">Instagram</span>
                  </a>

                  <a
                    href="https://www.linkedin.com/in/iderosinedeumaga/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-linkedin-line text-blue-600 text-2xl"></i>
                    </div>
                    <span className="font-medium text-gray-900">LinkedIn</span>
                  </a>

                  <a
                    href="https://www.facebook.com/profile.php?id=61552240756967&locale=fr_FR"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-facebook-line text-blue-700 text-2xl"></i>
                    </div>
                    <span className="font-medium text-gray-900">Facebook</span>
                  </a>

                  <a
                    href="http://tiktok.com/@ideroro2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-tiktok-line text-gray-900 text-2xl"></i>
                    </div>
                    <span className="font-medium text-gray-900">TikTok</span>
                  </a>
                </div>
              </div>

              {/* Newsletter */}
              <div className="bg-orange-600 rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">
                  Newsletter Ideevents
                </h2>
                <p className="text-orange-100 mb-6">
                  Restez informé de nos derniers événements, projets et actualités culturelles.
                </p>
                
                <form onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter-form" className="space-y-4">
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-white focus:outline-none text-sm"
                      placeholder="Votre adresse email"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-white text-orange-600 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    S'abonner à la newsletter
                  </button>

                  {newsletterStatus && (
                    <div className={`p-3 rounded-lg text-sm ${
                      newsletterStatus.includes('réussie') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {newsletterStatus}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nous trouver
            </h2>
            <p className="text-gray-600">
              Notre agence est située au cœur de Nantes, facilement accessible en transport en commun.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2709.8234567890123!2d-1.5534!3d47.2184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDEzJzA2LjIiTiAxwrAzMycxMi4yIlc!5e0!3m2!1sfr!2sfr!4v1234567890123"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation Ideevents"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
